// services/supabaseProgressService.ts
import { supabase } from './supabaseAudioService';
import {
  UserProgress,
  LanguageCode,
  serializeUserProgress,
  deserializeUserProgress
} from '../types/types';
import { SESSION_DATE } from '../hooks/useDailyStreak';

// 🆕 Interface pour les données de phrases
interface SentenceData {
  progress?: any[];
  sentences?: any[];
  metadata?: {
    lastUpdated: number;
    totalSessions: number;
    totalSentences: number;
    language: string;
  };
}

class SupabaseProgressService {
  private loadCache: Map<string, {
    data: any;
    timestamp: number;
  }> = new Map();

  // Stockage persistant des données de phrases chargées depuis Supabase
  // (survit à l'expiration du cache court-terme pour protéger les sauvegardes)
  private lastLoadedSentenceData: Map<string, SentenceData> = new Map();

  private readonly CACHE_DURATION = 5000; // 5 secondes
  
  /**
   * ✅ Charge la progression depuis Supabase avec fallback localStorage automatique
   * 🆕 MODIFIÉ : Charge aussi les données de phrases
   */
  async loadProgress(languageCode: LanguageCode): Promise<{
    userProgress: UserProgress | null;
    completedCategories: string[];
    totalXP: number;
    sentenceData?: SentenceData; // 🆕 AJOUTÉ
  }> {
    // ✅ Protection contre appels multiples
    const cached = this.loadCache.get(languageCode);
    const now = Date.now();
    
    // ✅ Si cache valide, retourner immédiatement
    if (cached && (now - cached.timestamp) < this.CACHE_DURATION) {
      return cached.data;
    }
    
   
    
    try {
      // 1. Récupérer la session utilisateur
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.user) {
return this.loadFromLocalStorage(languageCode);
      }

      // 2. Requête Supabase
      const { data, error } = await supabase
        .from('user_progress')
        .select('progress_data, total_xp, completed_categories, updated_at')
        .eq('user_id', session.user.id)
        .eq('language_code', languageCode)
        .maybeSingle();

      if (error) {
return this.loadFromLocalStorage(languageCode);
      }

      if (!data) {
       
        return this.loadFromLocalStorage(languageCode);
      }

     

      // 3. ⚠️ CRITIQUE: Désérialiser les données
      const rawProgressData = data.progress_data as any;

      // ✅ DÉSÉRIALISATION - Conversion Array → Set
      const userProgress = deserializeUserProgress(rawProgressData);

      // 4. EXTRAIRE ET RESTAURER LES DONNÉES DE PHRASES ET CONVERSATIONS
      const sentenceData = rawProgressData?.exercises?.sentenceConstruction as SentenceData;

      const convProgress = rawProgressData?.conversationProgress as Record<string, number> | undefined;
      if (convProgress) {
        Object.entries(convProgress).forEach(([convId, done]) => {
          localStorage.setItem(`${languageCode}-conv-${convId}`, JSON.stringify(done));
        });
      }

      if (sentenceData) {
        const sentenceProgressKey = `sentence-progress-${languageCode}`;
        const sentenceRevisionKey = `sentence-revision-${languageCode}-sessionHistory`;

        // Restaurer les sessions de progression
        if (sentenceData.progress && Array.isArray(sentenceData.progress)) {
          try {
            localStorage.setItem(sentenceProgressKey, JSON.stringify(sentenceData.progress));
            // Notifier CategorySelection même si sentences est vide
            window.dispatchEvent(new CustomEvent('sentenceProgressSynced', {
              detail: {
                language: languageCode,
                totalSessions: sentenceData.progress.length,
                source: 'Supabase',
                sessions: sentenceData.progress
              }
            }));
          } catch (localError) {
}
        }

        // Restaurer les phrases individuelles pour révision
        if (sentenceData.sentences && Array.isArray(sentenceData.sentences)) {
          try {
            localStorage.setItem(sentenceRevisionKey, JSON.stringify(sentenceData.sentences));
          } catch (localError) {
}
        }
      }

      const result = {
        userProgress,
        completedCategories: data.completed_categories || [],
        totalXP: data.total_xp || 0,
        sentenceData
      };

      // Mettre en cache le résultat (court-terme)
      this.loadCache.set(languageCode, {
        data: result,
        timestamp: now
      });

      // Stocker les données de phrases de façon persistante pour protéger les sauvegardes ultérieures
      if (sentenceData) {
        this.lastLoadedSentenceData.set(languageCode, sentenceData);
      }
     

      return result;

    } catch (error) {
      return this.loadFromLocalStorage(languageCode);
    }
  }

  /**
   * Fallback: Charger depuis localStorage
   * 🆕 MODIFIÉ : Charge aussi les données de phrases
   */
  private loadFromLocalStorage(languageCode: LanguageCode): {
    userProgress: UserProgress | null;
    completedCategories: string[];
    totalXP: number;
    sentenceData?: SentenceData; // 🆕 AJOUTÉ
  } {
   
    
    try {
      const storageKey = `${languageCode}-progress`;
      const savedProgress = localStorage.getItem(storageKey);
      
      if (!savedProgress) {
      
        return {
          userProgress: null,
          completedCategories: [],
          totalXP: 0,
          sentenceData: undefined
        };
      }

      const parsed = JSON.parse(savedProgress);
      const userProgress = deserializeUserProgress(parsed);
      
      const totalXP = parseInt(localStorage.getItem(`${languageCode}-totalXP`) || '0');
      const completedCategories = JSON.parse(
        localStorage.getItem(`${languageCode}-completedCategories`) || '[]'
      );

      // 🆕 CHARGER LES DONNÉES DE PHRASES
      const sentenceProgressKey = `sentence-progress-${languageCode}`;
      const sentenceRevisionKey = `sentence-revision-${languageCode}-sessionHistory`;
      
      let sentenceData: SentenceData | undefined = undefined;
      
      try {
        const progressData = localStorage.getItem(sentenceProgressKey);
        const revisionData = localStorage.getItem(sentenceRevisionKey);
        
        if (progressData || revisionData) {
          sentenceData = {
            progress: progressData ? JSON.parse(progressData) : [],
            sentences: revisionData ? JSON.parse(revisionData) : [],
            metadata: {
              lastUpdated: Date.now(),
              totalSessions: progressData ? JSON.parse(progressData).length : 0,
              totalSentences: revisionData ? JSON.parse(revisionData).length : 0,
              language: languageCode
            }
          };
        
        }
      } catch (sentenceError) {
}

   

      return {
        userProgress,
        completedCategories,
        totalXP,
        sentenceData // 🆕 AJOUTÉ
      };

    } catch (error) {
return {
        userProgress: null,
        completedCategories: [],
        totalXP: 0,
        sentenceData: undefined
      };
    }
  }


  async saveProgress(
    languageCode: LanguageCode,
    userProgress: UserProgress,
    completedCategories: string[],
    totalXP: number,
    sentenceData?: SentenceData // 🆕 PARAMÈTRE OPTIONNEL AJOUTÉ
  ): Promise<boolean> {
   
    
    try {
      // 1. Vérifier la session
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.user) {
return false;
      }

     

      // 2. Sérialiser les données (Sets → Arrays pour JSON)
      const serializedProgress = serializeUserProgress(userProgress);

      // Garde-fou anti-effacement : une progression vide (état React remis à
      // zéro pendant un chargement, un échec de chargement ou une déconnexion)
      // ne doit jamais écraser une progression existante sur le serveur.
      const isEmptyProgress =
        totalXP <= 0 &&
        completedCategories.length === 0 &&
        (serializedProgress.learnedWords?.length ?? 0) === 0 &&
        (serializedProgress.recentlyLearnedWords?.length ?? 0) === 0 &&
        Object.keys(serializedProgress.wordProgress ?? {}).length === 0;
      if (isEmptyProgress) {
        const { data: existing, error: existingError } = await supabase
          .from('user_progress')
          .select('total_xp, completed_categories, progress_data')
          .eq('user_id', session.user.id)
          .eq('language_code', languageCode)
          .maybeSingle();
        if (existingError) return false; // dans le doute, ne rien écraser
        const pd = (existing?.progress_data ?? {}) as any;
        const serverHasData = !!existing && (
          (existing.total_xp ?? 0) > 0 ||
          (existing.completed_categories?.length ?? 0) > 0 ||
          (pd.learnedWords?.length ?? 0) > 0 ||
          Object.keys(pd.wordProgress ?? {}).length > 0
        );
        if (serverHasData) return false;
      }
      
      


      // 4. SI PAS DE SENTENCEDATA FOURNI, CHERCHER DANS localStorage PUIS CACHE
      let finalSentenceData = sentenceData;

      if (!finalSentenceData) {
        const sentenceProgressKey = `sentence-progress-${languageCode}`;
        const sentenceRevisionKey = `sentence-revision-${languageCode}-sessionHistory`;

        const progressData = localStorage.getItem(sentenceProgressKey);
        const revisionData = localStorage.getItem(sentenceRevisionKey);

        if (progressData || revisionData) {
          // localStorage disponible → utiliser directement
          finalSentenceData = {
            progress: progressData ? JSON.parse(progressData) : [],
            sentences: revisionData ? JSON.parse(revisionData) : [],
            metadata: {
              lastUpdated: Date.now(),
              totalSessions: progressData ? JSON.parse(progressData).length : 0,
              totalSentences: revisionData ? JSON.parse(revisionData).length : 0,
              language: languageCode
            }
          };
        } else {
          // localStorage vide (effacé par clearProgressLocalStorage)
          // → utiliser le stockage persistant pour éviter d'écraser les données Supabase
          const persistent = this.lastLoadedSentenceData.get(languageCode);
          if (persistent) {
            finalSentenceData = persistent;
          } else {
            // fallback : cache court-terme si disponible
            const cached = this.loadCache.get(languageCode);
            if (cached?.data?.sentenceData) {
              finalSentenceData = cached.data.sentenceData;
            }
          }
        }
      }

      // 5. CONSTRUIRE L'OBJET PROGRESS_DATA AVEC EXERCISES
      const convProgress: Record<string, number> = {};
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith(`${languageCode}-conv-`)) {
          const convId = key.slice(`${languageCode}-conv-`.length);
          try { convProgress[convId] = JSON.parse(localStorage.getItem(key)!); } catch {}
        }
      }

      const progressDataToSave = {
        ...serializedProgress,
        exercises: {
          ...(serializedProgress as any).exercises,
          sentenceConstruction: finalSentenceData
        },
        conversationProgress: convProgress,
      };

      // Sauvegarder les données journalières dans la ligne globale (fire-and-forget)
      this.saveGlobalDailyData(languageCode, totalXP);

      

      // 6. Upsert dans Supabase avec timeout
      const timeoutPromise = new Promise<never>((_, reject) => 
        setTimeout(() => reject(new Error('Timeout Supabase')), 10000)
      );

      const savePromise = supabase
        .from('user_progress')
        .upsert({
          user_id: session.user.id,
          language_code: languageCode,
          progress_data: progressDataToSave, // 🆕 MODIFIÉ : Inclut maintenant les phrases
          total_xp: totalXP,
          completed_categories: completedCategories,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'user_id,language_code'
        });

      const { error } = await Promise.race([savePromise, timeoutPromise]);

      if (error) {
        return false;
      }

    
      
      // Invalider le cache après sauvegarde réussie
      this.loadCache.delete(languageCode);
      // Mettre à jour le stockage persistant avec les nouvelles données de phrases
      if (finalSentenceData) {
        this.lastLoadedSentenceData.set(languageCode, finalSentenceData);
      }
  

      return true;

    } catch (error) {
      return false;
    }
  }

  /**
   * Vérifie si une progression existe pour une langue
   */
  async hasProgress(languageCode: LanguageCode): Promise<boolean> {
  
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.user) {
       
        const storageKey = `${languageCode}-progress`;
        return !!localStorage.getItem(storageKey);
      }

      const { data, error } = await supabase
        .from('user_progress')
        .select('id')
        .eq('user_id', session.user.id)
        .eq('language_code', languageCode)
        .maybeSingle();

      if (error) {
return false;
      }

      const exists = !!data;
    
      
      return exists;

    } catch (error) {
return false;
    }
  }


async deleteProgress(languageCode: LanguageCode): Promise<boolean> {
 
  
  try {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session?.user) {
return false;
    }

    // 1️⃣ Supprimer de Supabase (user_progress + user_revision)
    const [{ error }, { error: revError }] = await Promise.all([
      supabase
        .from('user_progress')
        .delete()
        .eq('user_id', session.user.id)
        .eq('language_code', languageCode),
      supabase
        .from('user_revision')
        .delete()
        .eq('user_id', session.user.id)
        .eq('language_code', languageCode),
    ]);

    if (error) {
return false;
    }
    if (revError) {
    }

   
    
    // 2️⃣ Nettoyer localStorage - TOUTES les variantes
    const keysToRemove = new Set<string>();
    
    // a) Clés standards
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith(`${languageCode}-`) || 
          key.startsWith(`${languageCode}_`)) {
        keysToRemove.add(key);
      }
    });
    
    // b) Clés avec le language code en préfixe inversé (toutes celles effectivement écrites)
    const sentenceKeys = [
      `sentence-progress-${languageCode}`,
      `sentence-revision-${languageCode}-sessionHistory`,
      `grammar-progress-${languageCode}`,
      `learn-sessions-${languageCode}`,
      `revision-${languageCode}-sessionHistory`,
      `revision-${languageCode}-progress`,
      `revision-${languageCode}-grammar`,
    ];
    
    sentenceKeys.forEach(key => {
      if (localStorage.getItem(key)) {
        keysToRemove.add(key);
       
      }
    });
    
    // c) Recherche dynamique (sécurité)
    Object.keys(localStorage).forEach(key => {
      if (key.includes(languageCode)) {
        keysToRemove.add(key);
      }
    });
    
    // Suppression effective
    keysToRemove.forEach(key => {
      try {
        localStorage.removeItem(key);
      } catch (error) {
}
    });
    
   
    
    // 3️⃣ Nettoyer sync-queue et sync-queue-failed pour cette langue
    try {
      const queue = JSON.parse(localStorage.getItem('sync-queue') || '[]');
      localStorage.setItem('sync-queue', JSON.stringify(
        queue.filter((item: any) => item.languageCode !== languageCode)
      ));
      const failed = JSON.parse(localStorage.getItem('sync-queue-failed') || '[]');
      localStorage.setItem('sync-queue-failed', JSON.stringify(
        failed.filter((item: any) => item.languageCode !== languageCode)
      ));
    } catch (_) {}

    // 4️⃣ Invalider le cache
    this.loadCache.delete(languageCode);
  
    
    // 4️⃣ Notifier les autres onglets/navigateurs
    try {
      const bc = new BroadcastChannel('jogolinga-sync');
      bc.postMessage({
        type: 'PROGRESS_DELETED',
        languageCode,
        timestamp: Date.now()
      });
      bc.close();
   
    } catch (bcError) {
}

    return true;

  } catch (error) {
return false;
  }
}

  /**
   * Sauvegarde les données journalières mutualisées (streak, historique, boucliers,
   * baseline XP par langue) dans une ligne language_code='_global' de user_progress.
   * Appelé en fire-and-forget depuis saveProgress pour ne pas bloquer la sauvegarde.
   */
  private async saveGlobalDailyData(languageCode: string, totalXP: number): Promise<void> {
    try {
      // Local-time date, matching CategorySelection.tsx's getDailyXP —
      // an ISO/UTC date here would disagree with the local baseline and
      // cause it to look stale on every save (see App.tsx's SESSION_DATE fix).
      const today = SESSION_DATE;

      // Lecture du localStorage AVANT tout await : appelée sans attendre par
      // saveProgress, cette fonction finissait de lire après la déconnexion, qui
      // a déjà effacé streak, historique et boucliers → ligne _global écrasée
      // avec streak null, historique vide et 0 bouclier.
      const xpStartRaw = localStorage.getItem(`${languageCode}-daily-xp-start`);
      const xpStart = xpStartRaw ? JSON.parse(xpStartRaw) : { date: today, xp: totalXP };

      const streakRaw = localStorage.getItem('jogolinga-daily-streak');
      const streak = streakRaw ? JSON.parse(streakRaw) : null;

      const actHistRaw = localStorage.getItem('jogolinga-activity-history');
      const activityHistory: string[] = actHistRaw ? JSON.parse(actHistRaw) : [];

      const shields = parseInt(localStorage.getItem('jogolinga-streak-shields') ?? '0', 10);

      const shownRaw = localStorage.getItem('jogolinga-shown-collectibles');
      const shownCollectibles: string[] = shownRaw ? JSON.parse(shownRaw) : [];
      const dailyGoal = parseInt(localStorage.getItem('jogolinga-daily-goal') ?? '50', 10);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) return;

      // Lire la ligne globale existante pour fusionner xpByLanguage
      const { data: existing } = await supabase
        .from('user_progress')
        .select('progress_data')
        .eq('user_id', session.user.id)
        .eq('language_code', '_global')
        .maybeSingle();

      const prev = (existing?.progress_data as any) ?? {};


      // Une baseline provisoire (XP local pas encore synchronisé) ne doit pas
      // écraser celle du serveur : les autres appareils la reprendraient.
      const xpByLanguage = xpStart.provisional
        ? { ...(prev.xpByLanguage ?? {}) }
        : { ...(prev.xpByLanguage ?? {}), [languageCode]: xpStart };

      // Fusion avec le serveur : un appareil resté ouvert avec des données
      // anciennes effaçait les jours et le streak enregistrés ailleurs.
      const mergedHistory = Array.from(new Set([
        ...(Array.isArray(prev.activityHistory) ? prev.activityHistory : []),
        ...activityHistory,
      ])).sort().slice(-30);
      const mergedStreak = prev.streak?.date && (!streak || prev.streak.date > (streak.date ?? ''))
        ? prev.streak
        : streak;


      await supabase
        .from('user_progress')
        .upsert({
          user_id: session.user.id,
          language_code: '_global',
          progress_data: { streak: mergedStreak, activityHistory: mergedHistory, shields, xpByLanguage, shownCollectibles, dailyGoal },
          total_xp: 0,
          completed_categories: [],
          updated_at: new Date().toISOString(),
        }, { onConflict: 'user_id,language_code' });
    } catch { /* ignore — non-bloquant */ }
  }

  /**
   * Charge et restaure dans localStorage les données journalières mutualisées
   * (streak, historique, boucliers, baseline XP de la langue demandée).
   * À appeler au démarrage AVANT loadProgress pour que la baseline soit en place
   * avant qu'App.tsx ne la réinitialise.
   */
  async loadDailyData(languageCode: string): Promise<void> {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) return;

      const { data } = await supabase
        .from('user_progress')
        .select('progress_data')
        .eq('user_id', session.user.id)
        .eq('language_code', '_global')
        .maybeSingle();

      if (!data?.progress_data) return;

      const g = data.progress_data as any;
      const today = SESSION_DATE;

      // Baseline XP pour cette langue (seulement si c'est pour aujourd'hui)
      const xpStart = g.xpByLanguage?.[languageCode];
      if (xpStart?.date === today) {
        const key = `${languageCode}-daily-xp-start`;
        // Always restore from Supabase — local value may have been written to 0
        // by getDailyXP before this async load completed (new browser race condition).
        localStorage.setItem(key, JSON.stringify(xpStart));
      }

      // Streak — prendre le plus récent
      if (g.streak) {
        const localRaw = localStorage.getItem('jogolinga-daily-streak');
        if (!localRaw) {
          localStorage.setItem('jogolinga-daily-streak', JSON.stringify(g.streak));
        } else {
          const local = JSON.parse(localRaw);
          if (g.streak.date > (local.date ?? '')) {
            localStorage.setItem('jogolinga-daily-streak', JSON.stringify(g.streak));
          }
        }
      }

      // Historique d'activité — union
      if (Array.isArray(g.activityHistory) && g.activityHistory.length) {
        const localRaw = localStorage.getItem('jogolinga-activity-history');
        const local: string[] = localRaw ? JSON.parse(localRaw) : [];
        const merged = [...new Set([...local, ...g.activityHistory])].sort().slice(-30);
        localStorage.setItem('jogolinga-activity-history', JSON.stringify(merged));
      }

      // Boucliers — max (ne jamais réduire)
      if (typeof g.shields === 'number') {
        const localShields = parseInt(localStorage.getItem('jogolinga-streak-shields') ?? '0', 10);
        if (g.shields > localShields) {
          localStorage.setItem('jogolinga-streak-shields', String(g.shields));
        }
      }

      // Collectibles déjà vus — union (ne jamais masquer ce qui a déjà été affiché)
      if (Array.isArray(g.shownCollectibles) && g.shownCollectibles.length) {
        const localRaw = localStorage.getItem('jogolinga-shown-collectibles');
        const local: string[] = localRaw ? JSON.parse(localRaw) : [];
        const merged = [...new Set([...local, ...g.shownCollectibles])];
        localStorage.setItem('jogolinga-shown-collectibles', JSON.stringify(merged));
      }

      // Objectif du jour — restaurer seulement si la valeur locale est absente
      if (typeof g.dailyGoal === 'number' && [20, 50, 100].includes(g.dailyGoal)) {
        if (!localStorage.getItem('jogolinga-daily-goal')) {
          localStorage.setItem('jogolinga-daily-goal', String(g.dailyGoal));
        }
      }
    } catch { /* ignore */ }
  }

  async saveDailyGoalPreference(goal: number): Promise<void> {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) return;

      const { data: existing } = await supabase
        .from('user_progress')
        .select('progress_data')
        .eq('user_id', session.user.id)
        .eq('language_code', '_global')
        .maybeSingle();

      const prev = (existing?.progress_data as any) ?? {};

      await supabase
        .from('user_progress')
        .upsert({
          user_id: session.user.id,
          language_code: '_global',
          progress_data: { ...prev, dailyGoal: goal },
          total_xp: 0,
          completed_categories: [],
          updated_at: new Date().toISOString(),
        }, { onConflict: 'user_id,language_code' });
    } catch { /* ignore — non-bloquant */ }
  }

  /**
   * Vide tous les caches en mémoire (loadCache + lastLoadedSentenceData).
   * À appeler au logout et juste avant de charger la progression d'un nouvel
   * utilisateur, pour éviter qu'un cache associé à un compte précédent soit
   * réutilisé pour un autre compte sur le même navigateur.
   */
  clearAllCaches(): void {
    this.loadCache.clear();
    this.lastLoadedSentenceData.clear();
  }

  /**
   * Liste toutes les langues avec progression
   */
  async listLanguagesWithProgress(): Promise<LanguageCode[]> {
   
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.user) {
return [];
      }

      const { data, error } = await supabase
        .from('user_progress')
        .select('language_code')
        .eq('user_id', session.user.id);

      if (error) {
return [];
      }

      const languages = data?.map(row => row.language_code as LanguageCode) || [];
    

      return languages;

    } catch (error) {
return [];
    }
  }

  /**
   * Synchronise localStorage vers Supabase
   * Utile pour migration ou récupération de données
   */
  async syncLocalToSupabase(languageCode: LanguageCode): Promise<boolean> {
 
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.user) {
return false;
      }

      // Charger depuis localStorage
      const local = this.loadFromLocalStorage(languageCode);
      
      if (!local.userProgress) {
      
        return false;
      }

      // Sauvegarder vers Supabase
      const success = await this.saveProgress(
        languageCode,
        local.userProgress,
        local.completedCategories,
        local.totalXP,
        local.sentenceData // 🆕 AJOUTÉ
      );

      if (success) {
      
      } else {
}

      return success;

    } catch (error) {
return false;
    }
  }

  /**
   * Debug: Affiche les détails de la progression
   */
  async debugProgress(languageCode: LanguageCode): Promise<void> {
  
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
    
      
      if (session?.user) {
      
        
        const { data, error } = await supabase
          .from('user_progress')
          .select('*')
          .eq('user_id', session.user.id)
          .eq('language_code', languageCode)
          .maybeSingle();

        if (error) {
} else if (data) {
      
          
          // 🆕 Debug phrases
          const sentenceData = data.progress_data?.exercises?.sentenceConstruction;
          if (sentenceData) {
        
          } else {
        
          }
        } else {
       
        }
      }

      // Vérifier localStorage global
      const storageKey = `${languageCode}-progress`;
      const localData = localStorage.getItem(storageKey);
      if (localData) {
        JSON.parse(localData);
      } else {
      
      }
      
      // 🆕 Vérifier localStorage phrases
      const sentenceProgressKey = `sentence-progress-${languageCode}`;
      const sentenceRevisionKey = `sentence-revision-${languageCode}-sessionHistory`;
      
      const progressData = localStorage.getItem(sentenceProgressKey);
      const revisionData = localStorage.getItem(sentenceRevisionKey);
      
    
      if (progressData) {
        JSON.parse(progressData);
      }
      if (revisionData) {
        JSON.parse(revisionData);
      }
      if (!progressData && !revisionData) {
       
      }

   

    } catch (error) {
}
  }
}

export default new SupabaseProgressService();